import Phaser from 'phaser';

import { formatItemName, getSubtypeDisplayName, getSlotDisplayName, getWeaponHandDisplayName } from '@shared/utils/items';
import { getClassDisplayName } from '@shared/types/character';
import { getItemTemplate, getAllItemTemplates, getSetDefinition, getActiveSetBonuses } from '@shared/data/items';
import { findAffixByModifierKey, findAffixById } from '@shared/data/affixes';
import { isUniqueModifierKey, getUniqueModifier } from '@shared/data/uniqueModifiers';
import { WEAPON_SUBTYPE_LABELS } from '@shared/data/modifierDisplay';
import { getEnchantmentDefinition } from '@shared/data/enchantments';
import { ASSETS_BASE_URL, AVAILABLE_INVENTORY_FONTS, GOLD_TEXT_COLOR, ItemRarity, ITEM_RARITY_COLORS } from '@shared/constants';

import type { ItemModifiers } from '@shared/types/item';
import { ArmorSubtype } from '@shared/types/item';
import { ARMOR_TYPE_LABEL_ES } from '@shared/data/armorClasses';
import type { InventorySlotData } from '@shared/types/messages';
import { GAME_FONT_FAMILY, MONOGRAM_BMP_FONT, snapMonogramBmpSize } from '@shared/constants';
import { ItemEffectManager } from '../../ui/ItemEffectManager';
import { createCloseButton, createUIIcon, getIconTexture, getStatIconName, getStatLabelEs, getRarityIconName, formatThousands } from '../../sprites/IconAtlases';
import { createUiButton, UI_BUTTON_PALETTES, type UiButton } from '../../ui/uiButton';

/**
 * Formatea un valor rolled de afijo para mostrar en tooltip.
 * - 'percent' → "+5%" (multiplica por 100, sin decimales)
 * - 'flat' → valor entero si es entero, hasta 2 decimales si no
 */
function formatAffixValue(value: number, displayFormat?: 'flat' | 'percent'): string {
  if (displayFormat === 'percent') {
    return `${Math.round(value * 100)}%`;
  }
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Helper para obtener la fuente configurada para números de inventario (cantidad y símbolo de equipado)
 */
function getInventoryFontFamily(scene: Phaser.Scene): string {
  // Intentar obtener desde las opciones guardadas en localStorage
  const optionsKey = 'aureum_game_options_v1';
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(optionsKey);
      if (raw) {
        const options = JSON.parse(raw);
        if (options?.inventoryFont) {
          const font = AVAILABLE_INVENTORY_FONTS.find((f: { id: string; name: string; family: string }) => f.id === options.inventoryFont);
          if (font) {
            return font.family;
          }
        }
      }
    }
  } catch (e) {
    // Si hay error, usar la fuente por defecto
  }
  // Por defecto usar Nunito Sans para números de inventario
  return AVAILABLE_INVENTORY_FONTS[0].family;
}

/**
 * Helper para agregar la fuente del juego y resolución alta a un estilo de texto.
 * La resolución alta hace que los textos se vean nítidos incluso con pixelArt: true.
 */
function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(scene: Phaser.Scene, style?: T): T {
  return {
    fontFamily: GAME_FONT_FAMILY,
    resolution: 2, // Resolución alta para textos nítidos
    ...style,
  } as T;
}

/**
 * Helper para agregar la fuente de inventario (solo para números de cantidad y símbolo de equipado)
 */
function withInventoryFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(scene: Phaser.Scene, style?: T): T {
  return {
    fontFamily: getInventoryFontFamily(scene),
    resolution: 2, // Resolución alta para textos nítidos
    ...style,
  } as T;
}

/**
 * Helper para aplicar configuración de texto nítido después de crear un objeto de texto.
 * Asegura que el texto use resolución alta incluso si no se especificó en el estilo.
 */
function applySharpText(textObj: Phaser.GameObjects.Text): Phaser.GameObjects.Text {
  if (!textObj.style.resolution || textObj.style.resolution < 2) {
    textObj.setResolution(2);
  }
  return textObj;
}

type InventoryPanelCallbacks = {
  onEquip(slotIndex: number, slot: InventorySlotData): void;
  onUnequip(slotIndex: number, slot: InventorySlotData): void;
  onDrop(slotIndex: number, slot: InventorySlotData): void;
  onUse(slotIndex: number, slot: InventorySlotData): void;
  onMove(x: number, y: number): void;
  onMoveItem(fromSlot: number, toSlot: number): void;
};

type InventoryPanelOptions = {
  initialX: number;
  initialY: number;
};

/** Alto (px) de los botones de acción del info-pane (más altos que los viejos ~20px). */
const ACTION_BTN_H = 30;
/** Separación horizontal (px) entre Equipar/Desequipar y Tirar (comparten fila). */
const ACTION_BTN_GAP = 10;

type InventoryPanelElements = {
  bg: Phaser.GameObjects.Rectangle;
  title: Phaser.GameObjects.Text;
  /** Sprite del ícono `backpack` a la izquierda del título (opcional). */
  titleIcon: Phaser.GameObjects.Sprite | null;
  panelX: number;
  panelY: number;
  panelWidth: number;
  panelHeight: number;
  cellSize: number;
  cellPadding: number;
  infoX: number;
  infoY: number;
};

export class InventoryPanel {
  private readonly scene: Phaser.Scene;

  private readonly callbacks: InventoryPanelCallbacks;

  private readonly slotRects: Map<number, Phaser.GameObjects.Rectangle> = new Map();

  private readonly slotTexts: Map<number, Phaser.GameObjects.Text> = new Map();

  private readonly slotEquippedIndicators: Map<number, Phaser.GameObjects.Text> = new Map();

  private readonly slotIcons: Map<number, Phaser.GameObjects.Sprite> = new Map();

  // Badge de categoría (arma/casco/armadura/anillo/...) en la esquina inferior-izq
  // de los slots con un item equipable. Vacío para consumibles/otros.
  private readonly slotTypeBadges: Map<number, Phaser.GameObjects.Sprite> = new Map();

  private readonly slots: Map<number, InventorySlotData> = new Map();

  private readonly effectManager: ItemEffectManager;

  private panel: InventoryPanelElements | null = null;

  private selectedSlot: number | null = null;

  // Textos del panel de detalle: BitmapText de Monogram (pixel-perfect). Ver makeMono()
  // y MONOGRAM_BMP_FONT. Antes eran Phaser.Text (TTF) y a tamaños fuera de la grilla
  // nativa de Monogram "la misma letra" se veía distinta por el rasterizado sub-pixel.
  private itemTitle: Phaser.GameObjects.BitmapText | null = null;
  // Ícono de rareza a la izquierda del nombre del item en el detalle.
  private rarityNameIcon: Phaser.GameObjects.Sprite | null = null;
  private rarityNameIconActive = false;

  private itemDetails: Phaser.GameObjects.BitmapText | null = null;

  private itemSetBonusHeader: Phaser.GameObjects.BitmapText | null = null;
  private itemEnchantName: Phaser.GameObjects.BitmapText | null = null;

  /**
   * BitmapText creados dinámicamente al seleccionar un item (meta info: slot,
   * hand, weapon type, rareza label+value, requires, description, cantidad).
   * Se destruyen y reconstruyen en cada selectSlot.
   */
  private dynamicDetailTexts: Phaser.GameObjects.BitmapText[] = [];
  // Sprites de iconos de stat que prefijan las líneas de afijos del detalle.
  // Mismo ciclo de vida que dynamicDetailTexts (se limpian/recrean por selectSlot).
  private dynamicDetailIcons: Phaser.GameObjects.Sprite[] = [];

  private readonly activeBonusTexts: Map<number, Phaser.GameObjects.BitmapText> = new Map();

  private itemDebugInfo: Phaser.GameObjects.BitmapText | null = null;

  // Debug (ID / Icono / Animación / Equipado en) del ítem seleccionado: ya no se dibuja en el
  // pie; se muestra en un tooltip al hover del ícono de info que va a la izquierda de la X.
  private currentDebugLines: string[] = [];
  private infoIcon: Phaser.GameObjects.Sprite | null = null;
  private debugTooltip: Phaser.GameObjects.Container | null = null;

  private equipButton: UiButton | null = null;

  private unequipButton: UiButton | null = null;

  private dropButton: UiButton | null = null;

  private useButton: UiButton | null = null;

  private closeButton: Phaser.GameObjects.Sprite | null = null;

  // Gold display en la esquina inferior-derecha (icono + número).
  private goldLabel: Phaser.GameObjects.Container | null = null;
  private goldLabelIcon: Phaser.GameObjects.Sprite | null = null;
  private goldLabelText: Phaser.GameObjects.BitmapText | null = null;
  private currentGold = 0;

  private isOpen = false;

  private isDragging = false;

  private dragOffsetX = 0;

  private dragOffsetY = 0;

  // Para detección de doble click
  private lastClickTime = 0;
  private lastClickSlot = -1;

  // Para drag and drop
  private dragStartSlot: number | null = null;
  private dragPreview: Phaser.GameObjects.Container | null = null;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private itemDragActive: boolean = false;
  private readonly DRAG_THRESHOLD = 15; // Píxeles mínimos para iniciar drag (aumentado para evitar activación accidental)

  private windowBlurHandler: (() => void) | null = null;

  constructor(scene: Phaser.Scene, callbacks: InventoryPanelCallbacks, options: InventoryPanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.effectManager = new ItemEffectManager(scene);
    this.buildPanel(options.initialX, options.initialY);
    this.setVisible(false);
    
    // Agregar listener para cuando la ventana pierde el foco durante drag and drop
    if (typeof window !== 'undefined') {
      this.windowBlurHandler = () => {
        if (this.isDraggingItem() || this.hasDragStartSlot()) {
          this.cancelDragAndDrop();
        }
      };
      window.addEventListener('blur', this.windowBlurHandler);
    }
  }

  toggle(): void {
    if (!this.panel) return;
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    if (!this.panel) return;
    this.setVisible(true);
    // Si había un slot seleccionado antes de cerrar, verificar que todavía tenga item y restaurarlo
    if (this.selectedSlot !== null) {
      const selectedData = this.slots.get(this.selectedSlot);
      if (selectedData) {
        // El slot todavía tiene un item, restaurar la selección
        this.selectSlot(this.selectedSlot);
      } else {
        // El slot ya no tiene item, resetear la selección (esto también restaura el estilo visual)
        this.resetSelectionUI();
      }
    } else if (!this.isOpen) {
      // Solo resetear si no había selección previa y es la primera vez que se abre
      // Asegurarse de que ningún rect esté visualmente seleccionado
      this.slotRects.forEach((rect, slotIndex) => {
        const isLocked = slotIndex >= 30;
        rect.setStrokeStyle(1, isLocked ? 0x555555 : 0x888888);
        rect.setFillStyle(isLocked ? 0x222222 : 0x000000, 0.8);
      });
      this.resetSelectionUI();
    }
  }

  close(): void {
    if (!this.panel) return;
    // Abortar cualquier drag en curso para no dejar el preview colgado en pantalla
    this.destroyDragPreview();
    this.dragStartSlot = null;
    this.itemDragActive = false;
    this.isOpen = false;
    this.setVisible(false);
    // NO resetear la selección al cerrar, solo ocultar el panel
    // Esto permite que la selección se mantenga cuando se vuelva a abrir
  }

  isOpenPanel(): boolean {
    return this.isOpen;
  }

  applyInventorySync(inventory: InventorySlotData[], selectSlot?: number): void {
    this.slots.clear();
    inventory.forEach((slot) => this.slots.set(slot.slotIndex, slot));
    this.updateInventoryUI();

    // Si se especifica un slot, seleccionarlo después de actualizar
    if (selectSlot !== undefined) {
      this.selectSlot(selectSlot);
    }
  }

  /**
   * Flash visual sobre el slot dado durante ~1s (verde brillante → vuelve al
   * stroke original). Usado por external callers (ej. SpellVendorPanel click
   * sobre un item requerido para mostrar dónde lo tenés en tu inventory).
   * Si el panel está cerrado o el slot no existe, no-op.
   */
  highlightSlot(slotIndex: number): void {
    const rect = this.slotRects.get(slotIndex);
    if (!rect) return;
    // Cambiar stroke a verde brillante por 1s, después restaurar al color
    // por default (el updateInventoryUI normal lo va a pintar correctamente
    // cuando ocurra un sync; lo dejamos como esquema gris claro acá).
    rect.setStrokeStyle(3, 0x00ff66);
    this.scene.time.delayedCall(1000, () => {
      if (rect.active) {
        rect.setStrokeStyle(1, 0x666666);
      }
    });
  }

  handleEquipToggle(): void {
    if (!this.isOpen) return;
    const slotData = this.getSelectedSlotData();
    if (!slotData || this.selectedSlot === null) return;
    if (slotData.equippedIn) {
      this.callbacks.onUnequip(this.selectedSlot, slotData);
    } else {
      this.callbacks.onEquip(this.selectedSlot, slotData);
    }
  }

  handleDropItem(): void {
    if (!this.isOpen) return;
    const slotData = this.getSelectedSlotData();
    if (!slotData || this.selectedSlot === null) return;
    this.callbacks.onDrop(this.selectedSlot, slotData);
  }

  /** Usa el item seleccionado si es usable (invocación o comida). Ver docs/SUMMONS.md. */
  handleUseItem(): void {
    if (!this.isOpen) return;
    const slotData = this.getSelectedSlotData();
    if (!slotData || this.selectedSlot === null) return;
    const tpl = getItemTemplate(slotData.itemId);
    if (!tpl?.useSummonProfileId && !tpl?.foodBuff) return;
    this.callbacks.onUse(this.selectedSlot, slotData);
  }

  handlePointerMove(pointer: Phaser.Input.Pointer): void {
    // Detectar inicio de drag si hay un dragStartSlot pero aún no está activo
    if (this.dragStartSlot !== null && !this.itemDragActive) {
      const deltaX = Math.abs(pointer.x - this.dragStartX);
      const deltaY = Math.abs(pointer.y - this.dragStartY);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      // Si el mouse se movió lo suficiente, iniciar el drag — pero SOLO si el slot de origen
      // tiene item. Sin item no hay nada que arrastrar: no activamos el drag (así no aparece el
      // cursor de drag ni se dispara onMoveItem desde un origen vacío en el pointerup).
      if (distance > this.DRAG_THRESHOLD) {
        const slotData = this.slots.get(this.dragStartSlot);
        if (slotData) {
          this.itemDragActive = true;
          // Deseleccionar el slot anterior si hay uno seleccionado
          if (this.selectedSlot !== null && this.selectedSlot !== this.dragStartSlot) {
            const prevRect = this.slotRects.get(this.selectedSlot);
            const prevData = this.slots.get(this.selectedSlot);
            if (prevRect) {
              const isLockedPrev = this.selectedSlot >= 30;
              // Restaurar borde por defecto
              prevRect.setStrokeStyle(1, isLockedPrev ? 0x555555 : 0x888888);
              prevRect.setFillStyle(isLockedPrev ? 0x222222 : 0x000000, 0.8);
            }
            // Resetear la selección
            this.selectedSlot = null;
            this.resetSelectionUI();
          }
          
          // Restaurar borde por defecto del slot de origen (si no está seleccionado)
          if (this.selectedSlot !== this.dragStartSlot) {
            const dragRect = this.slotRects.get(this.dragStartSlot);
            if (dragRect) {
              const isLockedDrag = this.dragStartSlot >= 30;
              dragRect.setStrokeStyle(1, isLockedDrag ? 0x555555 : 0x888888);
            }
          }
          this.createDragPreview(this.dragStartSlot, pointer.x, pointer.y);
        }
      }
    }
    
    // Actualizar posición del preview si el drag está activo
    // Esto se hace globalmente para que funcione incluso cuando el mouse sale del slot
    if (this.dragPreview && this.itemDragActive) {
      // Usar setPosition para actualizar la posición del contenedor
      this.dragPreview.setPosition(pointer.x, pointer.y);
    }

    // Mover panel si está siendo arrastrado
    if (!this.isDragging || !this.panel) return;
    const newX = pointer.x - this.dragOffsetX;
    const newY = pointer.y - this.dragOffsetY;
    this.move(newX, newY);
    this.callbacks.onMove(newX, newY);
  }

  handlePointerUp(pointer: Phaser.Input.Pointer): void {
    // Limpiar drag preview si existe (por si se suelta fuera de un slot).
    // Incluir el caso en que el preview quedó vivo aunque el estado se desincronizó.
    if (this.dragStartSlot !== null || this.dragPreview) {
      this.dragStartSlot = null;
      this.itemDragActive = false;
      this.destroyDragPreview();
    }

    if (this.isDragging) {
      this.isDragging = false;
      pointer.event.stopPropagation();
    }
  }

  isDraggingPanel(): boolean {
    return this.isDragging;
  }

  isDraggingItem(): boolean {
    return this.itemDragActive && this.dragStartSlot !== null;
  }

  hasDragStartSlot(): boolean {
    return this.dragStartSlot !== null;
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    if (!this.panel || !this.isOpen) return false;
    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    const left = panelX - panelWidth / 2;
    const right = panelX + panelWidth / 2;
    const top = panelY - panelHeight / 2;
    const bottom = panelY + panelHeight / 2;
    return pointer.x >= left && pointer.x <= right && pointer.y >= top && pointer.y <= bottom;
  }

  private buildPanel(initialX: number, initialY: number): void {
    const { width, height } = this.scene.scale;
    const panelWidth = 550; // Aumentado para acomodar slots de 64x64
    // Bajado 400→380 al sacar el bloque debug del pie (ahora va a tooltip). La grilla de 8 filas
    // (≈344px) fija el mínimo práctico; el detalle gana aire porque la reserva de botones bajó.
    const panelHeight = 380;
    const panelX = initialX ?? width / 2;
    const panelY = initialY ?? height / 2;

    const bg = this.scene.add
      .rectangle(panelX, panelY, panelWidth, panelHeight, 0x111111, 0.9)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(850)
      .setScrollFactor(0)
      // Fondo interactivo: absorbe hover/click de objetos del mundo debajo
      // (Phaser topOnly) para que la ventana no deje pasar clicks ni cambie el cursor.
      .setInteractive();

    // Sin título visible "Inventario" (pedido de Dalmiro). Se mantiene el objeto como
    // BARRA DE DRAG invisible arriba-centro (texto vacío + tamaño fijo → conserva el área
    // de agarre para mover la ventana). La X queda absoluta en la esquina, no depende de esto.
    const title = this.scene.add
      .text(panelX, panelY - panelHeight / 2 + 16, '', withGameFont(this.scene, {
        fontSize: '32px',
        color: '#ffffff',
      }))
      .setFixedSize(240, 32)
      .setOrigin(0.5, 0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(851)
      .setScrollFactor(0);

    // Ya no se dibuja el ícono backpack (iba pegado al título quitado).
    const titleIcon: Phaser.GameObjects.Sprite | null = null;

    const gridX = panelX - panelWidth / 2 + 20;
    const gridY = panelY - panelHeight / 2 + 40;
    const cellSize = 36;
    const cellPadding = 2;

    for (let row = 0; row < 8; row += 1) {
      for (let col = 0; col < 5; col += 1) {
        const slotIndex = row * 5 + col;
        const x = gridX + col * (cellSize + cellPadding) + cellSize / 2;
        const y = gridY + row * (cellSize + cellPadding) + cellSize / 2;
        const isLocked = row >= 6;

        const rect = this.scene.add
          .rectangle(x, y, cellSize, cellSize, isLocked ? 0x222222 : 0x000000, 0.8)
          .setOrigin(0.5)
          .setStrokeStyle(1, isLocked ? 0x555555 : 0x888888)
          .setInteractive({ useHandCursor: !isLocked })
          .setDepth(851)
          .setScrollFactor(0);

        // Icono del item (inicialmente invisible, tamaño 32x32)
        // Crear sprite con textura placeholder
        // La textura correcta se asignará en loadAndShowIcon cuando sea necesario
        const icon = this.scene.add
          .sprite(x, y, 'item_icon_noicon')
          .setOrigin(0.5)
          .setDisplaySize(32, 32) // Los iconos son 32x32
          .setDepth(851)
          .setScrollFactor(0)
          .setVisible(false)
          .setActive(false);

        // Texto de cantidad (posición inicial: esquina inferior derecha, se ajustará dinámicamente)
        // Usa fuente de inventario (configurable en opciones)
        const text = this.scene.add
          .text(x + cellSize / 2 - 4, y + cellSize / 2 - 4, '', withInventoryFont(this.scene, {
            fontSize: '11px',
            color: isLocked ? '#555555' : '#ffffff',
            stroke: '#000000',
            strokeThickness: 2,
          }))
          .setOrigin(1, 1)
          .setDepth(852)
          .setScrollFactor(0);

        // Indicador de equipado "+" (esquina superior izquierda, más pegado al borde)
        // Usa fuente de inventario (configurable en opciones)
        const equippedIndicator = this.scene.add
          .text(x - cellSize / 2 + 1, y - cellSize / 2 + 1, '+', withInventoryFont(this.scene, {
            fontSize: '12px',
            color: isLocked ? '#555555' : '#00ff00',
            stroke: '#000000',
            strokeThickness: 2,
          }))
          .setOrigin(0, 0)
          .setDepth(853)
          .setScrollFactor(0)
          .setVisible(false);

        // Badge de categoría del item (esquina inferior-izquierda). Textura real
        // se asigna en updateInventoryUI según el tipo del item equipable.
        const typeBadge = this.scene.add
          .sprite(x - cellSize / 2 + 2, y + cellSize / 2 - 4, 'item_icon_noicon')
          .setOrigin(0, 1)
          .setDisplaySize(16, 16)
          .setDepth(853)
          .setScrollFactor(0)
          .setVisible(false);

        if (!isLocked) {
          // Pointer down - guardar posición inicial pero no iniciar drag todavía
          rect.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
            // Limpiar cualquier preview/estado residual de un drag anterior que no
            // se haya cerrado (evita huérfanos por pointerdown espurio durante drag).
            if (this.dragPreview) {
              this.destroyDragPreview();
              this.itemDragActive = false;
            }
            const slotData = this.slots.get(slotIndex);
            // Guardar posición inicial para detectar si realmente se está arrastrando
            // Solo permitir drag si hay un item, pero permitir selección de slots vacíos
            if (slotData) {
              this.dragStartSlot = slotIndex;
              // Guardar posición en coordenadas de pantalla (no world)
              // pointer.x y pointer.y son coordenadas de pantalla cuando setScrollFactor(0)
              this.dragStartX = pointer.x;
              this.dragStartY = pointer.y;
              this.itemDragActive = false;
            } else {
              // Para slots vacíos, solo guardar el slot para permitir selección
              // pero no permitir drag (no hay item para arrastrar)
              this.dragStartSlot = slotIndex;
            }
            pointer.event.stopPropagation();
          });

          // Drag end (pointerup)
          rect.on('pointerup', (pointer: Phaser.Input.Pointer) => {
            const now = Date.now();
            const slotData = this.slots.get(slotIndex);
            
            // Si había un drag activo (realmente se movió el mouse)
            if (this.dragStartSlot !== null && this.itemDragActive) {
              if (this.dragStartSlot !== slotIndex) {
                // Mover/intercambiar a un slot diferente
                const fromSlot = this.dragStartSlot;
                this.callbacks.onMoveItem(fromSlot, slotIndex);
              }
              // Si se suelta en el mismo slot, simplemente cancelar el drag (el item vuelve a su posición)
              this.dragStartSlot = null;
              this.itemDragActive = false;
              this.destroyDragPreview();
              pointer.event.stopPropagation();
              return;
            }

            // Si no había drag real (solo fue un click), verificar doble click o seleccionar
            if (this.dragStartSlot === slotIndex && !this.itemDragActive) {
              // Fue un click simple, no un drag
              if (slotData) {
                // Slot con item: verificar doble click o seleccionar
                const isDoubleClick = 
                  slotIndex === this.lastClickSlot && 
                  (now - this.lastClickTime) < 300;
                
                if (isDoubleClick) {
                  // Doble click: equipar o desequipar según el estado
                  if (slotData.equippedIn) {
                    // Item equipado: desequipar
                    this.callbacks.onUnequip(slotIndex, slotData);
                  } else if (this.canEquipItem(slotData.itemId)) {
                    // Item no equipado y equipable: equipar
                    this.callbacks.onEquip(slotIndex, slotData);
                  }
                  this.lastClickTime = 0; // Reset para evitar triple clicks
                  this.lastClickSlot = -1;
                } else {
                  // Click simple: seleccionar inmediatamente
                  this.selectSlot(slotIndex);
                  this.lastClickTime = now;
                  this.lastClickSlot = slotIndex;
                }
              } else {
                // Slot vacío: solo seleccionar
                this.selectSlot(slotIndex);
                this.lastClickTime = now;
                this.lastClickSlot = slotIndex;
              }
            }

            // Resetear estado de drag
            this.dragStartSlot = null;
            this.itemDragActive = false;
            
            pointer.event.stopPropagation();
          });

          // Nota: La detección de inicio de drag y actualización del preview
          // se hace en handlePointerMove global para que funcione incluso
          // cuando el mouse sale del slot
        }

        this.slotRects.set(slotIndex, rect);
        this.slotTexts.set(slotIndex, text);
        this.slotEquippedIndicators.set(slotIndex, equippedIndicator);
        this.slotIcons.set(slotIndex, icon);
        this.slotTypeBadges.set(slotIndex, typeBadge);
      }
    }

    const initialInfoX = gridX + 5 * (cellSize + cellPadding) + 24;
    const initialInfoY = gridY;

    this.itemTitle = this.makeMono(initialInfoX, initialInfoY, 'Ningún item seleccionado', {
      fontSize: '26px', // → 32 (×2 crisp): el nombre es el único tier "grande" del panel
      color: '#ffffff',
      wordWrap: { width: panelWidth / 2 + 20 },
    });

    // Ícono de rareza a la izquierda del nombre (en el gap a la izq de infoX).
    this.rarityNameIcon = this.scene.add
      .sprite(initialInfoX, initialInfoY, 'item_icon_noicon')
      .setOrigin(1, 0.5)
      .setDisplaySize(22, 22)
      .setDepth(851)
      .setScrollFactor(0)
      .setVisible(false);

    this.itemDetails = this.makeMono(initialInfoX, initialInfoY + 20, '', {
      fontSize: '20px', // → 16 (×1 nativo)
      color: '#cccccc',
      wordWrap: { width: panelWidth / 2 + 20 },
    });

    // Texto separado para "Bonus Set: X/Y" (el bold se ignora: Monogram es de un solo peso)
    this.itemSetBonusHeader = this.makeMono(initialInfoX, initialInfoY + 20, '', {
      fontSize: '20px',
      color: '#cccccc',
      wordWrap: { width: panelWidth / 2 + 20 },
    }).setVisible(false);

    // Información de debug (ID, animación, icono) en la parte inferior. Origin (0,1)
    // (anclado abajo-izquierda), distinto del default de makeMono.
    this.itemDebugInfo = this.makeMono(initialInfoX, panelY + panelHeight / 2 - 30, '', {
      fontSize: '16px',
      color: '#888888',
      wordWrap: { width: panelWidth / 2 + 20 },
    }).setOrigin(0, 1);

    // Botones de acción del info-pane: Equipar/Desequipar y Tirar comparten la MISMA fila (uno al
    // lado del otro), Usar va en una fila propia arriba; el layout final (anclado al fondo, por
    // encima de las 3 líneas de debug) lo hace updateButtonPositions. Ancho: dos mitades del ancho
    // del info-pane con un gap; Usar ocupa el ancho completo. Estética estilo login (ver uiButton.ts).
    const infoRightX = panelX + panelWidth / 2 - 16;
    const actionFullW = Math.max(80, infoRightX - initialInfoX);
    const actionHalfW = (actionFullW - ACTION_BTN_GAP) / 2;
    const actionH = ACTION_BTN_H;
    const ACTION_DEPTH = 851;

    this.equipButton = createUiButton(this.scene, {
      label: 'Equipar', palette: UI_BUTTON_PALETTES.equip, width: actionHalfW, height: actionH, depth: ACTION_DEPTH,
      onClick: () => {
        const slotData = this.getSelectedSlotData();
        if (slotData && this.selectedSlot !== null) this.callbacks.onEquip(this.selectedSlot, slotData);
      },
    });

    this.unequipButton = createUiButton(this.scene, {
      label: 'Desequipar', palette: UI_BUTTON_PALETTES.unequip, width: actionHalfW, height: actionH, depth: ACTION_DEPTH,
      onClick: () => {
        const slotData = this.getSelectedSlotData();
        if (slotData && this.selectedSlot !== null) this.callbacks.onUnequip(this.selectedSlot, slotData);
      },
    });

    this.dropButton = createUiButton(this.scene, {
      label: 'Tirar al piso', palette: UI_BUTTON_PALETTES.drop, width: actionHalfW, height: actionH, depth: ACTION_DEPTH,
      onClick: () => {
        const slotData = this.getSelectedSlotData();
        if (slotData && this.selectedSlot !== null) this.callbacks.onDrop(this.selectedSlot, slotData);
      },
    });

    // Botón "Usar" — sólo para items usables (invocación). Ver docs/SUMMONS.md.
    this.useButton = createUiButton(this.scene, {
      label: 'Usar', palette: UI_BUTTON_PALETTES.use, width: actionFullW, height: actionH, depth: ACTION_DEPTH,
      onClick: () => {
        const slotData = this.getSelectedSlotData();
        if (slotData && this.selectedSlot !== null) this.callbacks.onUse(this.selectedSlot, slotData);
      },
    });

    // Arrancan ocultos: updateActionButtonsVisibility los muestra según la selección.
    this.equipButton.setVisible(false);
    this.unequipButton.setVisible(false);
    this.dropButton.setVisible(false);
    this.useButton.setVisible(false);

    // Gold label = icono pegado al número, dorado. Anclado abajo-izquierda (debajo de los slots).
    const invGoldTex = getIconTexture('gold');
    this.goldLabelIcon = this.scene.add
      .sprite(0, 0, invGoldTex?.textureKey ?? '', invGoldTex?.frame ?? 0)
      .setOrigin(0, 0.5)
      .setDisplaySize(22, 22)
      .setScrollFactor(0)
      .setDepth(852);
    this.goldLabelText = this.makeMono(28, 0, '0', {
      fontSize: '22px', // → 16
      color: GOLD_TEXT_COLOR,
    })
      .setOrigin(0, 0.5)
      .setDepth(852);
    const goldLabelPos = this.computeGoldLabelPosition(panelX, panelY, panelWidth, panelHeight);
    this.goldLabel = this.scene.add
      .container(goldLabelPos.x, goldLabelPos.y, [this.goldLabelIcon, this.goldLabelText])
      .setDepth(852)
      .setScrollFactor(0);

    this.closeButton = createCloseButton(this.scene, panelX + panelWidth / 2 - 14, panelY - panelHeight / 2 + 14, 14)!
      .setOrigin(0.5, 0)
      .setDepth(852)
      .setAlpha(0.75);

    this.closeButton.on('pointerover', () => this.closeButton?.setAlpha(1));
    this.closeButton.on('pointerout', () => this.closeButton?.setAlpha(0.75));

    this.closeButton.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      this.close();
      pointer.event.stopPropagation();
    });

    // Ícono de INFO a la izquierda de la X: hover → tooltip con el debug (ID/Icono/Anim/Equipado en).
    this.infoIcon = createUIIcon(this.scene, panelX + panelWidth / 2 - 14 - 26, panelY - panelHeight / 2 + 14, 'wiki_book', 14);
    if (this.infoIcon) {
      this.infoIcon.setOrigin(0.5, 0).setDepth(852).setAlpha(0.7).setScrollFactor(0)
        .setInteractive({ useHandCursor: true });
      this.infoIcon.on('pointerover', () => { this.infoIcon?.setAlpha(1); this.showDebugTooltip(); });
      this.infoIcon.on('pointerout', () => { this.infoIcon?.setAlpha(0.7); this.hideDebugTooltip(); });
      this.infoIcon.setVisible(false); // se muestra al seleccionar un ítem
    }

    title.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.panel) return;
      this.isDragging = true;
      this.dragOffsetX = pointer.x - this.panel!.panelX;
      this.dragOffsetY = pointer.y - this.panel!.panelY;
      pointer.event.stopPropagation();
    });

      this.panel = {
        bg,
        title,
        titleIcon,
        panelX,
        panelY,
        panelWidth,
        panelHeight,
        cellSize,
        cellPadding,
        infoX: initialInfoX,
        infoY: initialInfoY,
      };
  }

  private updateInventoryUI(): void {
    // Solo actualizar si el panel está abierto
    const isPanelOpen = this.isOpen;
    
    if (!this.panel) return;
    const { cellSize } = this.panel;
    
    this.slotTexts.forEach((text, slotIndex) => {
      const data = this.slots.get(slotIndex);
      const icon = this.slotIcons.get(slotIndex);
      const rect = this.slotRects.get(slotIndex);
      
      if (data) {
        // Obtener template del item para verificar si es único (no stackeable)
        const itemTemplate = getItemTemplate(data.itemId);
        // Por defecto todos los items son stackeables, excepto si tienen unique: true
        const isUnique = itemTemplate?.unique ?? false;
        const isStackable = !isUnique;
        
        // Mostrar cantidad: si es stackeable, siempre mostrar (incluso si es 1)
        // Si es único, solo mostrar si es > 1
        const quantityText = isStackable ? `${data.quantity}` : (data.quantity > 1 ? `${data.quantity}` : '');
        text.setText(quantityText);
        text.setVisible(isPanelOpen && quantityText !== '');

        // Si el item no es stackeable y tiene cantidad > 1, mostrar en esquina superior izquierda
        // Si es stackeable, mantener en esquina inferior derecha
        if (rect && quantityText !== '') {
          if (!isStackable && data.quantity > 1) {
            // Esquina superior izquierda
            text.setPosition(rect.x - cellSize / 2 + 4, rect.y - cellSize / 2 + 4);
            text.setOrigin(0, 0);
          } else {
            // Esquina inferior derecha (comportamiento por defecto)
            text.setPosition(rect.x + cellSize / 2 - 4, rect.y + cellSize / 2 - 4);
            text.setOrigin(1, 1);
          }
        }

        // Mostrar indicador de equipado separado
        const equippedIndicator = this.slotEquippedIndicators.get(slotIndex);
        if (equippedIndicator) {
          equippedIndicator.setVisible(isPanelOpen && !!data.equippedIn);
        }

        // Restaurar borde por defecto (sin colores de rarity, los efectos visuales se encargan de eso)
        if (rect && this.selectedSlot !== slotIndex) {
          const isLocked = slotIndex >= 30;
          rect.setStrokeStyle(1, isLocked ? 0x555555 : 0x888888);
        }

        // Cargar y mostrar icono solo si el panel está abierto
        const typeBadge = this.slotTypeBadges.get(slotIndex);
        if (itemTemplate && icon) {
          const iconKey = this.getItemIconKey(itemTemplate.icon);
          if (isPanelOpen) {
            this.loadAndShowIcon(icon, iconKey, itemTemplate.icon);

            // Badge de categoría: sólo items equipables con icono mapeado.
            if (typeBadge && rect) {
              const typeIconName = this.getItemTypeIconName(itemTemplate);
              const badgeTex = typeIconName ? getIconTexture(typeIconName) : null;
              if (badgeTex && this.scene.textures.exists(badgeTex.textureKey)) {
                typeBadge.setTexture(badgeTex.textureKey, badgeTex.frame);
                typeBadge.setPosition(rect.x - cellSize / 2 + 2, rect.y + cellSize / 2 - 4);
                typeBadge.setVisible(true);
              } else {
                typeBadge.setVisible(false);
              }
            }

            // Crear o actualizar efecto visual según rolledRarity de la instancia
            // y su encantamiento. COMMON no tiene efecto visual.
            if (rect) {
              this.effectManager.updateEffect(
                slotIndex,
                rect.x,
                rect.y,
                cellSize,
                data.itemId,
                data.rolledRarity as ItemRarity,
                data.enchantId
              );
              this.effectManager.setEffectVisible(slotIndex, true);
            }
          } else {
            icon.setVisible(false);
            typeBadge?.setVisible(false);
            this.effectManager.setEffectVisible(slotIndex, false);
          }
        } else if (icon) {
          icon.setVisible(false);
          typeBadge?.setVisible(false);
          // Eliminar efecto si el slot está vacío
          this.effectManager.removeEffect(slotIndex);
        }
      } else {
        // Slot vacío: restaurar borde por defecto
        if (rect && this.selectedSlot !== slotIndex) {
          const isLocked = slotIndex >= 30;
          rect.setStrokeStyle(1, isLocked ? 0x555555 : 0x888888);
        }
        
        text.setText('');
        text.setVisible(false);
        const equippedIndicator = this.slotEquippedIndicators.get(slotIndex);
        if (equippedIndicator) {
          equippedIndicator.setVisible(false);
        }
        if (icon) {
          icon.setVisible(false);
        }
        this.slotTypeBadges.get(slotIndex)?.setVisible(false);
        // Eliminar efecto si el slot está vacío
        this.effectManager.removeEffect(slotIndex);
      }
    });

    // Si hay un slot seleccionado, verificar que todavía tenga un item
    if (this.selectedSlot !== null) {
      const selectedData = this.slots.get(this.selectedSlot);
      if (selectedData) {
        // El slot todavía tiene un item, mantener la selección
        this.selectSlot(this.selectedSlot);
      } else {
        // El slot ya no tiene item, resetear la selección (esto también restaura el estilo visual)
        this.resetSelectionUI();
      }
    } else {
      // Asegurarse de que ningún rect esté visualmente seleccionado
      // Pero mantener los colores de rarity para cada slot
      this.slotRects.forEach((rect, slotIndex) => {
        const slotData = this.slots.get(slotIndex);
        const isLocked = slotIndex >= 30;
        
        // Restaurar borde por defecto (los efectos visuales muestran la rarity)
        rect.setStrokeStyle(1, isLocked ? 0x555555 : 0x888888);
        
        rect.setFillStyle(isLocked ? 0x222222 : 0x000000, 0.8);
      });
      this.resetSelectionUI();
    }
  }

  /**
   * Genera la key de Phaser para un icono de item
   */
  private getItemIconKey(iconName: string): string {
    return `item_icon_${iconName}`;
  }

  /**
   * Nombre de icono de categoría (atlas UI) para un item EQUIPABLE, o null si
   * no es equipable o su tipo no tiene icono mapeado. Reusa los nombres del
   * atlas (ver IconAtlases): subtipos de arma + shield/helmet/armor/boots/ring/amulet.
   */
  private getItemTypeIconName(tpl: ReturnType<typeof getItemTemplate>): string | null {
    if (!tpl) return null;
    if (tpl.slot === 'weapon') {
      if (tpl.subtype === 'axe' && tpl.weaponHand === 'two') return 'axe_2h';
      const known = new Set(['sword', 'dagger', 'axe', 'staff', 'wand', 'bow', 'crossbow', 'throwable']);
      return tpl.subtype && known.has(tpl.subtype) ? tpl.subtype : null;
    }
    switch (tpl.slot) {
      case 'shield': return 'shield';
      case 'head': return 'helmet';
      case 'body': return 'armor';
      case 'boots': return 'boots';
      case 'ring': return 'ring';
      case 'amulet': return 'amulet';
      default: return null;
    }
  }

  /**
   * Muestra un icono de item en un slot (el icono ya debe estar precargado)
   * Si el icono no existe, muestra noicon.png como fallback
   */
  private loadAndShowIcon(
    iconSprite: Phaser.GameObjects.Sprite,
    iconKey: string,
    iconName: string
  ): void {
    // Los iconos ya están precargados en GameScene.preload()
    // Solo verificamos que exista y lo mostramos
    // IMPORTANTE: Solo mostrar si el panel está abierto
    if (!this.isOpen) {
      iconSprite.setVisible(false);
      return;
    }

    // Verificar si el icono existe y no es missing
    let useIcon = false;
    if (this.scene.textures.exists(iconKey)) {
      const texture = this.scene.textures.get(iconKey);
      // Verificar que la textura existe, no es missing, y tiene frames válidos
      if (texture && texture.key !== '__MISSING') {
        try {
          // Intentar obtener el frame para verificar que la textura está lista
          const frame = texture.get(0);
          if (frame && frame.width > 0 && frame.height > 0) {
            useIcon = true;
            iconSprite.setTexture(iconKey);
            // setTexture preserva el scale (no el displaySize): re-forzar 32×32 (tamaño
            // nativo del ícono) garantiza que TODOS los slots se vean 1:1 y consistentes,
            // igual que MerchantPanel.applyIcon. Sin esto, un ícono de tamaño nativo distinto
            // heredaría el scale previo y saldría más grande/chico.
            iconSprite.setDisplaySize(32, 32);
            iconSprite.setVisible(true);
          } else {
            // Frame inválido, la textura no está lista
            console.warn(`[InventoryPanel] Texture ${iconKey} (${iconName}) has invalid frame`);
          }
        } catch (e) {
          // Si hay error al acceder al frame, la textura no está lista
          console.warn(`[InventoryPanel] Texture ${iconKey} (${iconName}) not ready yet:`, e);
          useIcon = false;
        }
      } else {
        console.warn(`[InventoryPanel] Texture ${iconKey} (${iconName}) is missing or invalid`);
      }
    } else {
      console.warn(`[InventoryPanel] Texture ${iconKey} (${iconName}) does not exist`);
    }

    // Si el icono no existe, no está listo, o es missing, usar noicon.png
    if (!useIcon) {
      if (this.scene.textures.exists('item_icon_noicon')) {
        try {
          const noiconTexture = this.scene.textures.get('item_icon_noicon');
          if (noiconTexture && noiconTexture.key !== '__MISSING') {
            const frame = noiconTexture.get(0);
            if (frame && frame.width > 0 && frame.height > 0) {
              iconSprite.setTexture('item_icon_noicon');
              iconSprite.setDisplaySize(32, 32); // consistencia 1:1 (ver nota arriba)
              iconSprite.setVisible(true);
            } else {
              iconSprite.setVisible(false);
            }
          } else {
            iconSprite.setVisible(false);
          }
        } catch (e) {
          // Si hay error, ocultar temporalmente
          iconSprite.setVisible(false);
        }
      } else {
        // Si incluso noicon.png no existe, ocultar el icono
        iconSprite.setVisible(false);
      }
    }
  }

  /**
   * Setea (o oculta) el ícono de rareza a la izquierda del nombre del item.
   * `infoX`/`infoY` = posición del `itemTitle` (origin 0,0); el ícono se ancla
   * a su izquierda (origin 1, 0.5), centrado con la altura del título.
   */
  private applyRarityNameIcon(rarity: number | undefined, infoX: number, infoY: number): void {
    if (!this.rarityNameIcon) return;
    const name = rarity !== undefined ? getRarityIconName(rarity) : null;
    const tex = name ? getIconTexture(name) : null;
    if (tex && this.scene.textures.exists(tex.textureKey)) {
      const titleH = this.itemTitle?.height ?? 14;
      this.rarityNameIcon
        .setTexture(tex.textureKey, tex.frame)
        // Re-aplicar displaySize: setTexture preserva el scale (no el tamaño)
        // entre íconos de distinta celda source, así que sin esto el ícono
        // hereda el scale del frame anterior y se renderiza más grande. Mismo
        // patrón que MerchantPanel.applyDetailRarityIcon (sino el tamaño del
        // ícono de rareza no coincide entre inventario y merchant).
        .setDisplaySize(11, 11)
        .setPosition(infoX - 4, infoY + titleH / 2)
        .setVisible(this.isOpen);
      this.rarityNameIconActive = true;
    } else {
      this.rarityNameIcon.setVisible(false);
      this.rarityNameIconActive = false;
    }
  }

  private resetSelectionUI(): void {
    // Restaurar el estilo visual del rect que estaba seleccionado (si había uno)
    if (this.selectedSlot !== null) {
      const prevRect = this.slotRects.get(this.selectedSlot);
      const prevData = this.slots.get(this.selectedSlot);
      if (prevRect) {
        const isLockedPrev = this.selectedSlot >= 30;
        // Restaurar borde por defecto
        prevRect.setStrokeStyle(1, isLockedPrev ? 0x555555 : 0x888888);
        prevRect.setFillStyle(isLockedPrev ? 0x222222 : 0x000000, 0.8);
      }
    }
    
    this.selectedSlot = null;
    if (this.itemTitle) {
      this.itemTitle.setText('Slot vacío');
    }
    this.rarityNameIcon?.setVisible(false);
    this.rarityNameIconActive = false;
    if (this.itemDetails) {
      this.itemDetails.setText('');
      this.itemDetails.setTint(0xcccccc); // Restaurar color por defecto
    }
    if (this.itemDebugInfo) {
      this.itemDebugInfo.setText('');
    }
    this.clearActiveBonusTexts();
    this.clearDynamicDetailTexts();
    this.updateActionButtonsVisibility();
  }

  private selectSlot(slotIndex: number): void {
    if (!this.panel) return;

    if (this.selectedSlot !== null) {
      const prevRect = this.slotRects.get(this.selectedSlot);
      const prevData = this.slots.get(this.selectedSlot);
      if (prevRect) {
        const isLockedPrev = this.selectedSlot >= 30;
        // Restaurar borde por defecto
        prevRect.setStrokeStyle(1, isLockedPrev ? 0x555555 : 0x888888);
        prevRect.setFillStyle(isLockedPrev ? 0x222222 : 0x000000, 0.8);
      }
    }

    this.selectedSlot = slotIndex;
    const rect = this.slotRects.get(slotIndex);
    if (rect) {
      // Cuando se selecciona, usar color de selección (amarillo)
      rect.setStrokeStyle(2, 0xffffaa);
      rect.setFillStyle(0x202020, 0.9);
    }

    // Limpieza temprana: si el slot está vacío o si cambiamos de selección,
    // los dynamic detail texts del item anterior deben desaparecer.
    this.clearDynamicDetailTexts();

    const data = this.slots.get(slotIndex);
    if (!data) {
      if (this.itemTitle) this.itemTitle.setText('Slot vacío');
      if (this.itemDetails) this.itemDetails.setText('');
      if (this.itemDebugInfo) this.itemDebugInfo.setText('');
      this.rarityNameIcon?.setVisible(false);
      this.rarityNameIconActive = false;
      this.updateActionButtonsVisibility();
      return;
    }

    const itemTemplate = getItemTemplate(data.itemId);
    
    const itemName = formatItemName(data.itemId, data.enchantId, data.rolledRarity, data.rolledAffixes);
    // Icono discreto de equipado (✓ verde) en la misma línea
    const equippedIcon = data.equippedIn ? ' ✓' : '';
    
    // Aplicar color según rolledRarity de la instancia (no del template).
    const rarity = data.rolledRarity as ItemRarity;
    const rarityColor = ITEM_RARITY_COLORS[rarity] !== undefined
      ? `#${ITEM_RARITY_COLORS[rarity].toString(16).padStart(6, '0')}`
      : '#ffffff';
    
    // Limpiar nombre de encantamiento anterior si existe
    if (this.itemEnchantName) {
      this.itemEnchantName.destroy();
      this.itemEnchantName = null;
    }
    
    // Calcular infoX e infoY basándose en la posición actual del panel
    const { panelX, panelY, panelWidth, panelHeight } = this.panel;
    const gridX = panelX - panelWidth / 2 + 20;
    const gridY = panelY - panelHeight / 2 + 40;
    const cellSize = 36;
    const cellPadding = 2;
    const infoX = gridX + 5 * (cellSize + cellPadding) + 24;
    const infoY = gridY;
    
    // Actualizar infoX e infoY en el objeto panel para que esté disponible para otros métodos
    this.panel.infoX = infoX;
    this.panel.infoY = infoY;
    
    // Nombre del ítem en Y FIJO (infoY). SIN ícono de rareza a la izquierda del nombre
    // (ya se muestra en la línea "Rareza:"): ocultamos el sprite si existiera.
    this.itemTitle?.setPosition(infoX, infoY);
    this.itemTitle?.setText(`${itemName}${equippedIcon}`);
    this.itemTitle?.setTint(Phaser.Display.Color.HexStringToColor(rarityColor).color);
    this.rarityNameIcon?.setVisible(false);
    this.rarityNameIconActive = false;

    // Nombre del encantamiento en Y FIJO (infoY + 30), no debajo del título (que varía).
    if (data.enchantId) {
      const enchantDef = getEnchantmentDefinition(data.enchantId);
      if (enchantDef && this.itemTitle) {
        const enchantName = enchantDef.name;

        this.itemEnchantName = this.makeMono(infoX, infoY + 30, enchantName, {
          fontSize: '18px', // → 16
          color: '#ffffff',
          wordWrap: { width: panelWidth / 2 + 20 },
        });
      }
    }
    
    // Nombre de rareza (rolledRarity de la instancia).
    const rarityLabels: Record<ItemRarity, string> = {
      [ItemRarity.COMMON]: 'Común',
      [ItemRarity.RARE]: 'Raro',
      [ItemRarity.EPIC]: 'Épico',
      [ItemRarity.LEGENDARY]: 'Legendario',
      [ItemRarity.SET]: 'Set',
    };
    const rarityName = rarityLabels[rarity] ?? `Rareza ${rarity}`;

    // detailsText (cuerpo central) lleva encantamiento + set info.
    // Los afijos rolled se renderizan APARTE como dynamic texts para poder
    // colorear cada tier individualmente.
    let detailsText = '';

    // Modificadores del encantamiento si existe (el nombre ya está en itemEnchantName)
    if (data.enchantId) {
      const enchantDef = getEnchantmentDefinition(data.enchantId);
      if (enchantDef) {
        const enchantModifiers = this.formatModifiers(enchantDef.modifiers);
        if (enchantModifiers && enchantModifiers !== 'Sin bonos') {
          if (detailsText.length > 0) detailsText += `\n\n`;
          detailsText += `Encantamiento: ${enchantModifiers}`;
        }
      }
    }
    
    // Información del set si el item pertenece a uno
    let isSetComplete = false;
    if (itemTemplate?.setId) {
      const setInfo = this.getSetInfo(itemTemplate.setId);
      if (setInfo) {
        const setEquippedCount = setInfo.equippedCount;
        const setTotalCount = setInfo.totalCount;
        isSetComplete = setEquippedCount === setTotalCount;
        
        const setDef = getSetDefinition(itemTemplate.setId);
        const setName = setDef?.name || itemTemplate.setId;

        // Espacio antes de la info del set sólo si detailsText ya tenía contenido.
        const setLead = detailsText.length > 0 ? `\n\n\n` : '';
        detailsText += `${setLead}Set: ${setName}`;
        detailsText += `\nBonus Set: ${setEquippedCount}/${setTotalCount}`;
        
        // Mostrar TODOS los bonos en el texto principal (para que las posiciones coincidan)
        // Los bonos activos se mostrarán en verde después con objetos Text separados
        if (setDef) {
          setDef.bonuses.forEach((bonus) => {
            const bonusText = bonus.description || this.formatModifiers(bonus.modifiers);
            // Formato de lista con bullet (•) como items de listado
            detailsText += `\n  • ${bonus.pieces} piezas: ${bonusText}`;
          });
        }
      }
    }
    
    // Limpiar overlays anteriores (dynamicDetailTexts ya se limpiaron arriba)
    this.clearActiveBonusTexts();

    // -------- ANCLAS FIJAS (relativas a infoY) --------
    // Cada campo tiene su Y FIJO para que NO salte al pasar de ítem; si un ítem no tiene un
    // campo, su lugar queda vacío pero los demás no se mueven. Font 20px (línea ~24). La banda
    // "cuerpo" (afijos → set → descripción) fluye local desde A_BODY. Cantidad va pinneada abajo.
    // Slot y Rareza van PRIMEROS (casi todos los ítems los tienen). Tipo y
    // Empuñadura (sólo armas) van después. Cada campo en Y FIJO para que no salte.
    const A_SLOT = infoY + 60;
    const A_RARITY = infoY + 84;
    const A_TYPE = infoY + 108;
    const A_HAND = infoY + 132;
    const A_BODY = infoY + 164;

    // -------- META BLOCK (Slot / Rareza / Tipo / Empuñadura) en Y FIJOS --------
    const isEquipment = itemTemplate?.type === 'equipment';
    const slotName = isEquipment ? getSlotDisplayName(itemTemplate?.slot) : null;
    const isWeapon = itemTemplate?.slot === 'weapon';
    const handName = isWeapon ? getWeaponHandDisplayName(itemTemplate?.weaponHand) : null;
    const weaponSubtypeName = isWeapon ? getSubtypeDisplayName(itemTemplate?.subtype) : null;

    const metaFontConfig = withGameFont(this.scene, {
      fontSize: '20px',
      color: '#cccccc',
      wordWrap: { width: panelWidth / 2 + 20 },
    });

    const typeIconName = isEquipment ? this.getItemTypeIconName(itemTemplate) : null;
    if (slotName) {
      const slotX = this.appendDetailIcon(typeIconName, infoX, A_SLOT);
      this.appendDetailLine(`Slot: ${slotName}`, slotX, A_SLOT, metaFontConfig);
    }
    if (handName) {
      this.appendDetailLine(`Empuñadura: ${handName}`, infoX, A_HAND, metaFontConfig);
    }
    if (weaponSubtypeName) {
      this.appendDetailLine(`Tipo: ${weaponSubtypeName}`, infoX, A_TYPE, metaFontConfig);
    }
    // Rareza SIEMPRE en su Y fijo.
    this.appendRarityLine(rarityName, rarityColor, infoX, A_RARITY, metaFontConfig, getRarityIconName(data.rolledRarity));

    // La banda de cuerpo (afijos / set / descripción) fluye local desde el ancla fija.
    let cursorY = A_BODY;

    // -------- AFIJOS ROLLED (RARE/EPIC) --------
    // Cada afijo es una línea con valor total + tag de tier(s). 1 componente:
    // `+15 HP [T2]`. 2 componentes mismo afijo: `+35 HP (T1 + T2)` con cada
    // tier coloreado individualmente.
    if (data.rolledAffixes && Object.keys(data.rolledAffixes).length > 0) {
      cursorY = this.appendDetailLine('Afijos:', infoX, cursorY + 4, metaFontConfig);
      const tierColors = ['#aaaaaa', '#e0c050', '#ffd060']; // T1 gris, T2 amarillo, T3 dorado
      const entries = Object.entries(data.rolledAffixes) as Array<[string, Array<{ v: number; t: number }>]>;
      for (const [key, components] of entries) {
        // Los afijos de Reino se keyean por id (comparten modifierKey); probar id primero.
        const affDef = findAffixById(key) ?? findAffixByModifierKey(key as keyof ItemModifiers);
        const label = affDef?.displayName ?? key;
        let total = 0;
        for (const c of components) total += c.v;
        const formatted = formatAffixValue(total, affDef?.displayFormat);
        const placeholderSuffix = affDef?.placeholder ? ' (próximamente)' : '';

        // Prefijo con el icono del stat (si está mapeado), igual que el panel de
        // personaje. Si hay icono, la línea arranca corrida a la derecha; si no,
        // mantiene el sangrado de dos espacios.
        const ICON_SIZE = 18;
        const ICON_GAP = 3;
        let lineX = infoX;
        const iconName = getStatIconName(key);
        const iconTex = iconName ? getIconTexture(iconName) : null;
        const hasIcon = !!(iconTex && this.scene.textures.exists(iconTex.textureKey));
        if (hasIcon && iconTex) {
          const sprite = this.scene.add
            .sprite(infoX + 2, cursorY + ICON_SIZE / 2 + 1, iconTex.textureKey, iconTex.frame)
            .setOrigin(0, 0.5)
            .setDisplaySize(ICON_SIZE, ICON_SIZE)
            .setDepth(851)
            .setScrollFactor(0);
          this.dynamicDetailIcons.push(sprite);
          lineX = infoX + 2 + ICON_SIZE + ICON_GAP;
        }
        const leadingPad = hasIcon ? '' : '  ';
        const chunks: Array<{ text: string; color: string }> = [
          { text: `${leadingPad}+${formatted} ${label}${placeholderSuffix} `, color: '#cccccc' },
        ];
        if (components.length === 1) {
          const t = components[0].t;
          chunks.push({ text: '[', color: '#cccccc' });
          chunks.push({ text: `T${t}`, color: tierColors[t - 1] ?? '#cccccc' });
          chunks.push({ text: ']', color: '#cccccc' });
        } else {
          chunks.push({ text: '(', color: '#cccccc' });
          for (let i = 0; i < components.length; i += 1) {
            const t = components[i].t;
            chunks.push({ text: `T${t}`, color: tierColors[t - 1] ?? '#cccccc' });
            if (i < components.length - 1) {
              chunks.push({ text: ' + ', color: '#cccccc' });
            }
          }
          chunks.push({ text: ')', color: '#cccccc' });
        }
        cursorY = this.appendChunkedLine(chunks, lineX, cursorY, metaFontConfig);
      }
    } else if (itemTemplate?.modifiers) {
      // Backstab crítico anidado por arma: renderizar como flag dorado (uno por arma
      // habilitada), antes del listado numérico que filtra objetos. Ver modifierDisplay.
      const backstab = itemTemplate.modifiers.backstabCrit;
      if (backstab) {
        for (const [sub, v] of Object.entries(backstab)) {
          if (typeof v !== 'number' || v <= 0) continue;
          const armaEs = WEAPON_SUBTYPE_LABELS[sub] ?? sub;
          cursorY = this.appendDetailLine(`◆ Golpe mortal por la espalda`, infoX, cursorY, {
            ...metaFontConfig,
            color: '#ffd060',
          });
          cursorY = this.appendDetailLine(
            `  Crítico garantizado al golpear la espalda con ${armaEs}.`,
            infoX,
            cursorY,
            { ...metaFontConfig, color: '#b7a25a' }
          );
        }
      }
      // Sin afijos rolled: listar los modifiers base del template con su ícono
      // de stat (mismo criterio que el ItemTooltip). Sólo los no-cero.
      const baseEntries = Object.entries(itemTemplate.modifiers)
        .filter(([, v]) => typeof v === 'number' && v !== 0) as Array<[string, number]>;
      for (const [key, val] of baseEntries) {
        // Modificadores ÚNICOS: renderizar nombre + descripción en dorado, sin el
        // valor numérico crudo (es un flag). Ver shared/data/uniqueModifiers.ts.
        if (isUniqueModifierKey(key)) {
          const uniq = getUniqueModifier(key);
          if (uniq) {
            cursorY = this.appendDetailLine(`◆ ${uniq.displayName}`, infoX, cursorY, {
              ...metaFontConfig,
              color: '#ffd060',
            });
            cursorY = this.appendDetailLine(`  ${uniq.description}`, infoX, cursorY, {
              ...metaFontConfig,
              color: '#b7a25a',
            });
            continue;
          }
        }
        const statX = this.appendDetailIcon(getStatIconName(key), infoX, cursorY);
        const pad = statX === infoX ? '  ' : '';
        const sign = val > 0 ? '+' : '';
        cursorY = this.appendDetailLine(`${pad}${sign}${val} ${getStatLabelEs(key)}`, statX, cursorY, metaFontConfig);
      }
    }

    // Gap antes del bloque central de encantamiento/set (itemDetails).
    const detailsY = detailsText.length > 0 ? cursorY + 6 : cursorY;

    this.itemDetails?.setPosition(infoX, detailsY);
    this.itemDetails?.setText(detailsText);
    this.itemDetails?.setTint(0xcccccc);
    
    // Crear objetos Text separados para los bonos activos en verde
    if (itemTemplate?.setId) {
      const setInfo = this.getSetInfo(itemTemplate.setId);
      const setDef = getSetDefinition(itemTemplate.setId);
      if (setInfo && setDef && this.itemDetails) {
        const setEquippedCount = setInfo.equippedCount;
        // Ref local: TS pierde el narrowing de this.itemDetails dentro del forEach.
        const details = this.itemDetails;

        // Calcular posiciones exactas para los bonos activos (verde)
        // Como detailsText ahora tiene TODOS los bonos, las posiciones coincidirán
        const textBeforeBonuses = detailsText.split('\n  •')[0]; // Texto hasta antes de los bonos

        // Construir texto acumulativo línea por línea para calcular posiciones exactas
        let accumulatedText = textBeforeBonuses;
        let activeBonusIndex = 0;

        setDef.bonuses.forEach((bonus) => {
          const isActive = setEquippedCount >= bonus.pieces;
          const bonusText = bonus.description || this.formatModifiers(bonus.modifiers);
          const bonusLineText = `  • ${bonus.pieces} piezas: ${bonusText}`;
          
          // Calcular la altura del texto antes de esta línea (medición con el mismo
          // BitmapText/tamaño para que coincida con el layout real).
          const wrapW = this.panel ? this.panel.panelWidth / 2 + 20 : 200;
          const tempBeforeLine = this.makeMono(0, 0, accumulatedText, {
            fontSize: '20px',
            color: '#cccccc',
            wordWrap: { width: wrapW },
          });
          const yPosForThisLine = tempBeforeLine.height;
          tempBeforeLine.destroy();

          if (isActive) {
            // BitmapText del bono activo en verde, posicionado donde cae en detailsText.
            const bonusTextObj = this.makeMono(
              details.x,
              details.y + yPosForThisLine,
              bonusLineText,
              {
                fontSize: '20px',
                color: '#00cc66', // Verde del set
                wordWrap: { width: wrapW },
              }
            ).setDepth(852); // Un poco más arriba que itemDetails para que se vea por encima

            this.activeBonusTexts.set(activeBonusIndex, bonusTextObj);
            activeBonusIndex++;
          }
          
          // Agregar esta línea al texto acumulativo para el siguiente cálculo
          accumulatedText += `\n${bonusLineText}`;
        });
      }
    }

    // -------- FOOTER BLOCK (Requiere / Descripción / Cantidad) --------
    // Se renderiza debajo de itemDetails (o debajo del meta block si itemDetails
    // está vacío). Cada sección es opcional excepto Cantidad.
    const detailsHeight = (this.itemDetails && detailsText.length > 0) ? this.itemDetails.height : 0;
    let footerY = detailsY + detailsHeight + (detailsHeight > 0 ? 8 : 0);

    const baseFooterStyle = withGameFont(this.scene, {
      fontSize: '20px',
      color: '#cccccc',
      wordWrap: { width: panelWidth / 2 + 20 },
    });

    // Requiere: lista de clases en español (sólo si requiredClasses no vacía)
    if (itemTemplate?.requiredClasses && itemTemplate.requiredClasses.length > 0) {
      const classNames = itemTemplate.requiredClasses
        .map((c: string) => getClassDisplayName(c))
        .join(', ');
      footerY = this.appendDetailLine(`Requiere: ${classNames}`, infoX, footerY, baseFooterStyle);
    }

    // Tipo de armadura (Tela/Cuero/Metal): informa qué clases pueden vestirla (jerarquía).
    if (
      (itemTemplate?.slot === 'head' || itemTemplate?.slot === 'body' || itemTemplate?.slot === 'shield') &&
      itemTemplate?.subtype &&
      ARMOR_TYPE_LABEL_ES[itemTemplate.subtype as ArmorSubtype]
    ) {
      const label = ARMOR_TYPE_LABEL_ES[itemTemplate.subtype as ArmorSubtype];
      footerY = this.appendDetailLine(`Armadura: ${label}`, infoX, footerY, baseFooterStyle);
    }

    // Descripción (italica, color más suave)
    if (itemTemplate?.description) {
      const descStyle = withGameFont(this.scene, {
        fontSize: '20px',
        color: '#aaaaaa',
        fontStyle: 'italic',
        wordWrap: { width: panelWidth / 2 + 20 },
      });
      footerY = this.appendDetailLine(itemTemplate.description, infoX, footerY + 4, descStyle);
    }

    // Cantidad: pegada JUSTO ARRIBA de los botones de acción (que van pegados al
    // borde inferior). Mismo cálculo que updateButtonPositions para no solaparse.
    // `footerY` se ignora a propósito para el pin.
    void footerY;
    const { panelY: pY, panelHeight: pH } = this.panel;
    const isUsableItem = !!itemTemplate?.useSummonProfileId || !!itemTemplate?.foodBuff;
    const btnRowTop = pY + pH / 2 - 14 - ACTION_BTN_H; // fila inferior (Equipar/Tirar)
    const btnBlockTop = isUsableItem ? btnRowTop - 6 - ACTION_BTN_H : btnRowTop; // techo de los botones visibles
    this.appendDetailLine(`Cantidad: ${data.quantity}`, infoX, btnBlockTop - 28, baseFooterStyle);

    // Información de debug (Equipado en, ID, animación, icono) en la parte inferior
    const debugLines: string[] = [];
    // Equipado en primero
    if (data.equippedIn) {
      debugLines.push(`Equipado en: ${data.equippedIn}`);
    }
    debugLines.push(`ID: ${data.itemId}`);
    
    // Información del sprite/animation que carga
    const spriteLines: string[] = [];
    if (itemTemplate?.weaponId) {
      spriteLines.push(`weapon${itemTemplate.weaponId}`);
    }
    if (itemTemplate?.bodyId) {
      spriteLines.push(`body${itemTemplate.bodyId}`);
    }
    if (itemTemplate?.shieldId) {
      spriteLines.push(`shield${itemTemplate.shieldId}`);
    }
    if (itemTemplate?.helmId) {
      spriteLines.push(`helm${itemTemplate.helmId}`);
    }
    if (itemTemplate?.bootId) {
      spriteLines.push(`boot${itemTemplate.bootId}`);
    }
    if (itemTemplate?.headId) {
      spriteLines.push(`head${itemTemplate.headId}`);
    }
    if (spriteLines.length > 0) {
      debugLines.push(`Animación: ${spriteLines.join(', ')}`);
    }
    
    // Nombre del icono
    if (itemTemplate?.icon) {
      debugLines.push(`Icono: ${itemTemplate.icon}`);
    }

    // Animación del item (campo `animation`), solo si está definida.
    if (itemTemplate?.animation) {
      debugLines.push(`Anim: ${itemTemplate.animation}`);
    }

    // El bloque debug (ID / Icono / Animación / Equipado en) ya no se dibuja en el pie:
    // se guarda para el tooltip del ícono de info al lado de la X (ver buildPanel).
    this.currentDebugLines = debugLines;
    this.infoIcon?.setVisible(this.isOpen);

    // Actualizar posiciones de los botones después de establecer el texto
    this.updateButtonPositions();

    this.updateActionButtonsVisibility();
  }

  /** Muestra el tooltip de debug (ID/Icono/Anim/Equipado en) bajo el ícono de info. */
  private showDebugTooltip(): void {
    this.hideDebugTooltip();
    if (!this.panel || this.currentDebugLines.length === 0) return;
    const { panelX, panelWidth, panelY, panelHeight } = this.panel;
    const x = panelX + panelWidth / 2 - 14 - 26;
    const y = panelY - panelHeight / 2 + 32;
    const PAD = 8;
    const text = this.makeMono(-PAD, PAD, this.currentDebugLines.join('\n'), {
      fontSize: '16px',
      color: '#dddddd',
      wordWrap: { width: 320 },
    }).setOrigin(1, 0);
    const bg = this.scene.add
      .rectangle(0, 0, text.width + PAD * 2, text.height + PAD * 2, 0x000000, 0.95)
      .setOrigin(1, 0)
      .setStrokeStyle(1, 0x666666)
      .setScrollFactor(0);
    this.debugTooltip = this.scene.add
      .container(x, y, [bg, text])
      .setDepth(1200)
      .setScrollFactor(0);
  }

  /** Oculta/destruye el tooltip de debug. */
  private hideDebugTooltip(): void {
    this.debugTooltip?.destroy(true);
    this.debugTooltip = null;
  }

  /**
   * Obtiene información sobre un set: cuántas piezas tiene en total y cuántas están equipadas
   */
  private getSetInfo(setId: string): { totalCount: number; equippedCount: number } | null {
    // Obtener todos los items que pertenecen a este set
    const allItems = getAllItemTemplates();
    const setItems = allItems.filter(item => item.setId === setId);
    
    if (setItems.length === 0) return null;
    
    // Contar cuántos items del set están equipados
    let equippedCount = 0;
    this.slots.forEach((slotData) => {
      if (slotData.equippedIn && setItems.some(item => item.id === slotData.itemId)) {
        equippedCount++;
      }
    });
    
    return {
      totalCount: setItems.length,
      equippedCount: equippedCount,
    };
  }

  /**
   * Formatea los modificadores de un item en texto legible
   */
  private formatModifiers(modifiers: import('@shared/types/item').ItemModifiers): string {
    const parts: string[] = [];
    
    if (modifiers.hp) parts.push(`+${modifiers.hp} HP`);
    if (modifiers.mana) parts.push(`+${modifiers.mana} Mana`);
    if (modifiers.minDamage && modifiers.maxDamage) {
      parts.push(`+${modifiers.minDamage}-${modifiers.maxDamage} Daño`);
    } else if (modifiers.minDamage) {
      parts.push(`+${modifiers.minDamage} Daño Mín`);
    } else if (modifiers.maxDamage) {
      parts.push(`+${modifiers.maxDamage} Daño Máx`);
    }
    if (modifiers.physDef) parts.push(`+${modifiers.physDef} Def Física`);
    if (modifiers.magicDef) parts.push(`+${modifiers.magicDef} Def Mágica`);
    if (modifiers.moveSpeed) parts.push(`+${modifiers.moveSpeed} Velocidad`);
    if (modifiers.attackSpeed) parts.push(`+${modifiers.attackSpeed} Vel Ataque`);
    if (modifiers.critChance) parts.push(`+${(modifiers.critChance * 100).toFixed(0)}% Crítico`);
    if (modifiers.critMultiplier) parts.push(`+${modifiers.critMultiplier}x Multi Crítico`);
    if (modifiers.evasion) parts.push(`+${(modifiers.evasion * 100).toFixed(0)}% Evasión`);
    if (modifiers.stamina) parts.push(`+${modifiers.stamina} Stamina`);
    if (modifiers.regenHP) parts.push(`+${modifiers.regenHP} Regen HP`);
    if (modifiers.regenMana) parts.push(`+${modifiers.regenMana} Regen Mana`);
    if (modifiers.regenStamina) parts.push(`+${modifiers.regenStamina} Regen Stamina`);
    
    return parts.length > 0 ? parts.join(', ') : 'Sin bonos';
  }

  private getSelectedSlotData(): InventorySlotData | null {
    if (this.selectedSlot === null) return null;
    return this.slots.get(this.selectedSlot) ?? null;
  }

  private canEquipItem(itemId: string): boolean {
    // Un item se puede equipar si tiene un prefijo que indica un slot de equipamiento
    // Verificar todos los slots: HEAD, BODY, WEAPON, SHIELD, RING, BACK, BOOTS, AMULET
    // Weapons ahora usan el formato: weapon_subtipo_00X (ej: weapon_sword_001, weapon_staff_001)
    return (
      itemId.startsWith('weapon_') ||
      itemId.startsWith('shield_') ||
      itemId.startsWith('armor_') ||
      itemId.startsWith('body_') ||
      itemId.startsWith('boots_') ||
      itemId.startsWith('helm_') ||
      itemId.startsWith('head_') ||
      itemId.startsWith('ring_') ||
      itemId.startsWith('back_') ||
      itemId.startsWith('cape_') ||
      itemId.startsWith('amulet_') ||
      itemId.startsWith('necklace_') ||
      itemId.startsWith('boat_')
    );
  }

  /**
   * Limpia y destruye todos los objetos Text de bonos activos
   */
  private clearActiveBonusTexts(): void {
    this.activeBonusTexts.forEach((textObj) => {
      textObj.destroy();
    });
    this.activeBonusTexts.clear();
  }

  /**
   * Destruye los Text objects dinámicos del detalle (meta info y footer info).
   * Se llama al inicio de selectSlot y al limpiar la selección.
   */
  private clearDynamicDetailTexts(): void {
    for (const t of this.dynamicDetailTexts) {
      t.destroy();
    }
    this.dynamicDetailTexts.length = 0;
    for (const s of this.dynamicDetailIcons) {
      s.destroy();
    }
    this.dynamicDetailIcons.length = 0;
  }

  /**
   * Crea un BitmapText de Monogram (pixel-perfect) a partir del MISMO objeto de
   * estilo que usaban los `add.text` del panel (`fontSize`/`color`/`wordWrap`), para
   * migrar los call-sites sin reescribir sus configs. Mapea `fontSize` → tamaño
   * crisp (múltiplo de 16 vía {@link snapMonogramBmpSize}) y `color` → tint. El
   * `fontStyle` bold/italic se ignora a propósito: Monogram es de un solo peso (el
   * bold/italic de la TTF era faux del browser). Origin (0,0), depth 851, scroll 0.
   */
  private makeMono(
    x: number,
    y: number,
    text: string,
    style: Phaser.Types.GameObjects.Text.TextStyle
  ): Phaser.GameObjects.BitmapText {
    const rawSize =
      typeof style.fontSize === 'number'
        ? style.fontSize
        : parseInt(String(style.fontSize ?? '16'), 10) || 16;
    const bt = this.scene.add
      .bitmapText(x, y, MONOGRAM_BMP_FONT, text, snapMonogramBmpSize(rawSize))
      .setOrigin(0, 0)
      .setDepth(851)
      .setScrollFactor(0);
    if (style.color) {
      bt.setTint(Phaser.Display.Color.HexStringToColor(String(style.color)).color);
    }
    const wrapWidth =
      style.wordWrap && typeof style.wordWrap.width === 'number' ? style.wordWrap.width : 0;
    if (wrapWidth > 0) {
      bt.setMaxWidth(wrapWidth);
    }
    return bt;
  }

  /**
   * Crea un BitmapText de detalle apilado verticalmente en (x, y), lo trackea para
   * limpieza futura, y devuelve el Y siguiente (debajo del texto creado).
   */
  private appendDetailLine(
    text: string,
    x: number,
    y: number,
    style: Phaser.Types.GameObjects.Text.TextStyle
  ): number {
    const obj = this.makeMono(x, y, text, style);
    this.dynamicDetailTexts.push(obj);
    return y + obj.height;
  }

  /**
   * Dibuja un ícono de 11px a la izquierda de una línea de detalle (si el nombre
   * está mapeado en el atlas). Trackea el sprite en `dynamicDetailIcons` para
   * limpieza por `selectSlot`. Devuelve el X donde debe arrancar el texto: corrido
   * a la derecha si hubo ícono, o el `x` original si no.
   */
  private appendDetailIcon(iconName: string | null, x: number, y: number): number {
    const ICON = 18;
    const GAP = 3;
    const tex = iconName ? getIconTexture(iconName) : null;
    if (tex && this.scene.textures.exists(tex.textureKey)) {
      const sprite = this.scene.add
        .sprite(x + 2, y + ICON / 2 + 1, tex.textureKey, tex.frame)
        .setOrigin(0, 0.5)
        .setDisplaySize(ICON, ICON)
        .setDepth(851)
        .setScrollFactor(0);
      this.dynamicDetailIcons.push(sprite);
      return x + 2 + ICON + GAP;
    }
    return x;
  }

  /**
   * Renderiza la línea "Rareza: [ícono] <value>": etiqueta gris + ícono de
   * rareza (opcional, entre label y valor) + valor en el color de la rareza.
   * Devuelve el Y siguiente.
   */
  private appendRarityLine(
    rarityName: string,
    rarityColor: string,
    x: number,
    y: number,
    baseStyle: Phaser.Types.GameObjects.Text.TextStyle,
    iconName?: string | null
  ): number {
    const labelText = 'Rareza: ';
    const label = this.makeMono(x, y, labelText, baseStyle);
    this.dynamicDetailTexts.push(label);

    // Ícono entre el label y el nombre de la rareza.
    let valueX = x + label.width;
    const tex = iconName ? getIconTexture(iconName) : null;
    if (tex && this.scene.textures.exists(tex.textureKey)) {
      const ICON = 18;
      const GAP = 3;
      const sprite = this.scene.add
        .sprite(valueX, y + label.height / 2, tex.textureKey, tex.frame)
        .setOrigin(0, 0.5)
        .setDisplaySize(ICON, ICON)
        .setDepth(851)
        .setScrollFactor(0);
      this.dynamicDetailIcons.push(sprite);
      valueX += ICON + GAP;
    }

    const valueStyle = { ...baseStyle, color: rarityColor };
    const value = this.makeMono(valueX, y, rarityName, valueStyle);
    this.dynamicDetailTexts.push(value);

    return y + Math.max(label.height, value.height);
  }

  /**
   * Renderiza una línea compuesta por N "chunks" (segmentos de texto con su
   * propio color), alineados horizontalmente en (x, y). Usado para mostrar
   * afijos rolled donde el texto principal va en gris y los tags `[T1]`,
   * `(T1 + T2)` van en color por tier. Devuelve el Y siguiente.
   */
  private appendChunkedLine(
    chunks: Array<{ text: string; color: string }>,
    x: number,
    y: number,
    baseStyle: Phaser.Types.GameObjects.Text.TextStyle
  ): number {
    let cursorX = x;
    let maxH = 0;
    for (const ch of chunks) {
      const style = { ...baseStyle, color: ch.color };
      const obj = this.makeMono(cursorX, y, ch.text, style);
      this.dynamicDetailTexts.push(obj);
      cursorX += obj.width;
      if (obj.height > maxH) maxH = obj.height;
    }
    return y + maxH;
  }

  /**
   * Actualiza las posiciones de los textos verdes de bonos activos cuando se reposiciona itemDetails
   */
  private updateActiveBonusTextPositions(): void {
    if (!this.itemDetails) return;
    // Ref local: TS pierde el narrowing de this.itemDetails dentro del forEach.
    const details = this.itemDetails;

    // Recalcular las posiciones basándose en la posición actual de itemDetails
    const slotData = this.getSelectedSlotData();
    if (!slotData) return;
    
    const itemTemplate = getItemTemplate(slotData.itemId);
    if (!itemTemplate?.setId) return;
    
    const setInfo = this.getSetInfo(itemTemplate.setId);
    const setDef = getSetDefinition(itemTemplate.setId);
    if (!setInfo || !setDef) return;
    
    const setEquippedCount = setInfo.equippedCount;
    const textBeforeBonuses = this.itemDetails.text.split('\n  •')[0];
    
    // Construir texto acumulativo para calcular posiciones
    let accumulatedText = textBeforeBonuses;
    let activeBonusIndex = 0;
    
    setDef.bonuses.forEach((bonus) => {
      const isActive = setEquippedCount >= bonus.pieces;
      const bonusText = bonus.description || this.formatModifiers(bonus.modifiers);
      const bonusLineText = `  • ${bonus.pieces} piezas: ${bonusText}`;
      
      if (isActive) {
        // Calcular la altura del texto antes de esta línea (mismo BitmapText/tamaño).
        const tempBeforeLine = this.makeMono(0, 0, accumulatedText, {
          fontSize: '20px',
          color: '#cccccc',
          wordWrap: { width: this.panel ? this.panel.panelWidth / 2 + 20 : 200 },
        });
        const yPosForThisLine = tempBeforeLine.height;
        tempBeforeLine.destroy();
        
        // Actualizar posición del texto verde
        const bonusTextObj = this.activeBonusTexts.get(activeBonusIndex);
        if (bonusTextObj) {
          bonusTextObj.setPosition(details.x, details.y + yPosForThisLine);
        }
        activeBonusIndex++;
      }
      
      // Agregar esta línea al texto acumulativo para el siguiente cálculo
      accumulatedText += `\n${bonusLineText}`;
    });
  }

  /**
   * Posiciona los botones de acción anclados al FONDO del info-pane, por ENCIMA de las 3 líneas de
   * debug (itemDebugInfo, ~30px sobre el borde inferior). La reserva es fija (no depende de la altura
   * variable del debug ni del orden de reposicionado en move()), así se mantienen estables al arrastrar.
   * Equipar/Desequipar comparten la fila inferior con Tirar (izquierda/derecha); Usar va en la fila de arriba.
   */
  private updateButtonPositions(): void {
    if (!this.panel || !this.itemDetails) return;

    const infoX = this.itemDetails.x;
    const panelBottom = this.panel.panelY + this.panel.panelHeight / 2;
    // El debug se movió a un tooltip: ya no hace falta reservar líneas abajo, así que los botones
    // bajan al borde (sólo un pequeño margen) y el detalle gana espacio.
    const DEBUG_RESERVE = 14;
    const rowBottom = panelBottom - DEBUG_RESERVE;

    // Fila inferior: [Equipar/Desequipar] a la izquierda, [Tirar] a la derecha (misma línea).
    const rowTop = rowBottom - (this.equipButton?.height ?? ACTION_BTN_H);
    this.equipButton?.setTopLeft(infoX, rowTop);
    this.unequipButton?.setTopLeft(infoX, rowTop);
    const dropX = infoX + (this.equipButton?.width ?? 0) + ACTION_BTN_GAP;
    this.dropButton?.setTopLeft(dropX, rowTop);

    // Fila de arriba: [Usar] (ancho completo), sólo visible para items usables.
    const useTop = rowTop - 6 - (this.useButton?.height ?? ACTION_BTN_H);
    this.useButton?.setTopLeft(infoX, useTop);
  }

  private updateActionButtonsVisibility(): void {
    const slotData = this.getSelectedSlotData();
    const hasSelection = this.isOpen && this.selectedSlot !== null && !!slotData;
    const isEquipped = !!slotData?.equippedIn;
    const template = slotData ? getItemTemplate(slotData.itemId) : undefined;
    const isUsable = !!template?.useSummonProfileId || !!template?.foodBuff;
    // Un item sin slot (ej. poción) no muestra "Equipar".
    const equipVisible = hasSelection && !isEquipped && !!template?.slot;
    const unequipVisible = hasSelection && isEquipped;
    const useVisible = hasSelection && isUsable;

    this.equipButton?.setVisible(equipVisible);
    this.unequipButton?.setVisible(unequipVisible);
    this.dropButton?.setVisible(hasSelection);
    this.useButton?.setVisible(useVisible);
    if (this.closeButton) {
      this.closeButton.setVisible(this.isOpen).setActive(this.isOpen);
    }
  }

  private setVisible(visible: boolean): void {
    if (!this.panel) return;
    // Red de seguridad: al ocultar el panel por cualquier vía, destruir el preview
    // de drag (container standalone en depth 900, no trackeado por el panel).
    if (!visible) {
      this.destroyDragPreview();
    }
    const panel = this.panel;
    panel.bg.setVisible(visible).setActive(visible);
    panel.title.setVisible(visible).setActive(visible);
    panel.titleIcon?.setVisible(visible).setActive(visible);

    this.slotRects.forEach((rect) => rect.setVisible(visible).setActive(visible));
    this.slotTexts.forEach((text) => text.setVisible(visible).setActive(visible));
    this.slotEquippedIndicators.forEach((indicator) => indicator.setVisible(visible).setActive(visible));
    // Badges: ocultar siempre acá; updateInventoryUI (al abrir) reactiva los que corresponden.
    this.slotTypeBadges.forEach((badge) => badge.setVisible(false).setActive(visible));
    
    // Los iconos solo se muestran si el panel está visible Y tienen un item
    this.slotIcons.forEach((icon, slotIndex) => {
      if (visible) {
        // Si el panel está visible, la visibilidad del icono se maneja en updateInventoryUI
        // No forzar aquí para evitar mostrar iconos vacíos
      } else {
        // Si el panel está oculto, ocultar todos los iconos y efectos inmediatamente
        // Asegurar que el icono esté completamente oculto y desactivado
        icon.setVisible(false).setActive(false);
        // Verificar que el icono tenga una posición válida (no en 0,0 por defecto)
        // Si está en (0,0), podría ser un problema de posicionamiento
        if (icon.x === 0 && icon.y === 0 && this.panel) {
          // Intentar obtener la posición correcta del slot
          const row = Math.floor(slotIndex / 5);
          const col = slotIndex % 5;
          const gridX = this.panel.panelX - this.panel.panelWidth / 2 + 20;
          const gridY = this.panel.panelY - this.panel.panelHeight / 2 + 40;
          const x = gridX + col * (this.panel.cellSize + this.panel.cellPadding) + this.panel.cellSize / 2;
          const y = gridY + row * (this.panel.cellSize + this.panel.cellPadding) + this.panel.cellSize / 2;
          icon.setPosition(x, y);
        }
        this.effectManager.setEffectVisible(slotIndex, false);
      }
    });

    this.itemTitle?.setVisible(visible).setActive(visible);
    this.rarityNameIcon?.setVisible(visible && this.rarityNameIconActive).setActive(visible);
    this.itemDetails?.setVisible(visible).setActive(visible);
    this.itemEnchantName?.setVisible(visible).setActive(visible);
    this.itemSetBonusHeader?.setVisible(visible && (this.itemSetBonusHeader?.visible ?? false)).setActive(visible && (this.itemSetBonusHeader?.visible ?? false));
    this.activeBonusTexts.forEach((textObj) => {
      textObj.setVisible(visible).setActive(visible);
    });
    this.dynamicDetailTexts.forEach((textObj) => {
      textObj.setVisible(visible).setActive(visible);
    });
    this.dynamicDetailIcons.forEach((iconObj) => {
      iconObj.setVisible(visible).setActive(visible);
    });
    this.itemDebugInfo?.setVisible(false).setActive(false); // ya no se dibuja (va a tooltip)
    // Ícono de info: sólo visible con el panel abierto Y un ítem seleccionado; su tooltip se cierra.
    this.infoIcon?.setVisible(visible && this.selectedSlot !== null).setActive(visible);
    if (!visible) this.hideDebugTooltip();

    this.goldLabel?.setVisible(visible).setActive(visible);
    this.goldLabelIcon?.setVisible(visible).setActive(visible);
    this.goldLabelText?.setVisible(visible).setActive(visible);

    this.isOpen = visible;
    this.updateActionButtonsVisibility();
    
    // Actualizar UI después de cambiar la visibilidad para que los iconos se muestren/oculten correctamente
    if (visible) {
      this.updateInventoryUI();
    }
  }

  private createDragPreview(slotIndex: number, x: number, y: number): void {
    const slotData = this.slots.get(slotIndex);
    if (!slotData) return;

    const icon = this.slotIcons.get(slotIndex);
    if (!icon || !icon.visible) return;

    // Crear un contenedor con el icono y un fondo semi-transparente
    const container = this.scene.add.container(x, y);
    container.setDepth(900);
    container.setScrollFactor(0);

    // Fondo semi-transparente
    const bg = this.scene.add
      .rectangle(0, 0, this.panel!.cellSize, this.panel!.cellSize, 0x000000, 0.7)
      .setOrigin(0.5)
      .setStrokeStyle(2, 0xffffaa);
    container.add(bg);

    // Copiar el icono
    const iconKey = icon.texture.key;
    if (iconKey && iconKey !== '__MISSING') {
      const dragIcon = this.scene.add
        .sprite(0, 0, iconKey)
        .setOrigin(0.5)
        .setDisplaySize(32, 32);
      container.add(dragIcon);
    }

    // Texto de cantidad si existe
    const text = this.slotTexts.get(slotIndex);
    if (text && text.text) {
      const dragText = this.scene.add
        .text(this.panel!.cellSize / 2 - 4, this.panel!.cellSize / 2 - 4, text.text, withInventoryFont(this.scene, {
          fontSize: '11px',
          color: '#ffffff',
          stroke: '#000000',
          strokeThickness: 2,
        }))
        .setOrigin(1, 1);
      container.add(dragText);
    }

    this.dragPreview = container;
  }

  private destroyDragPreview(): void {
    if (this.dragPreview) {
      this.dragPreview.destroy();
      this.dragPreview = null;
    }
  }

  /**
   * Cancela el drag and drop actual y restaura el slot a su estado normal
   */
  private cancelDragAndDrop(): void {
    // Guardar el slot que estaba siendo arrastrado antes de resetear
    const slotIndexToSelect = this.dragStartSlot;
    
    // Limpiar el drag preview
    this.destroyDragPreview();
    
    // Resetear estado de drag primero
    this.dragStartSlot = null;
    this.itemDragActive = false;
    
    // Seleccionar el slot que estaba siendo arrastrado
    if (slotIndexToSelect !== null) {
      this.selectSlot(slotIndexToSelect);
    }
  }

  /**
   * Posición del label de oro: esquina inferior-derecha del panel, con padding.
   * El container está en (x,y) y los hijos (icono + texto) se alinean a partir
   * de ahí. Origen del container = ancla izquierda del set; restamos un ancho
   * estimado para que quede pegado al borde derecho.
   */
  private computeGoldLabelPosition(
    panelX: number,
    panelY: number,
    panelWidth: number,
    panelHeight: number
  ): { x: number; y: number } {
    // El oro va ABAJO-IZQUIERDA, debajo de la tabla de slots. El ícono (origen
    // 0,0.5 = borde izq) se alinea al MISMO borde izquierdo que la grilla (gridX),
    // "a la misma distancia a su izquierda que la tabla de slots". Ver grid en
    // renderInventory: gridX/gridY, 8 filas de cellSize+cellPadding.
    const gridX = panelX - panelWidth / 2 + 20;
    const gridY = panelY - panelHeight / 2 + 40;
    const cellSize = 36;
    const cellPadding = 2;
    const gridBottom = gridY + 8 * (cellSize + cellPadding); // borde inferior de la grilla
    return {
      x: gridX,
      y: gridBottom + 20, // centro vertical del label, debajo de los slots
    };
  }

  /**
   * Actualiza el monto de oro mostrado en la esquina del panel. Llamado por
   * GameScene cuando entra GoldUpdatedMessage o al abrir el panel.
   */
  setGold(gold: number): void {
    this.currentGold = gold;
    if (!this.goldLabelText) return;
    this.goldLabelText.setText(formatThousands(gold));
    // Re-anclar al borde derecho ahora que el texto cambió de ancho.
    if (this.panel && this.goldLabel) {
      const pos = this.computeGoldLabelPosition(
        this.panel.panelX,
        this.panel.panelY,
        this.panel.panelWidth,
        this.panel.panelHeight
      );
      this.goldLabel.setPosition(pos.x, pos.y);
    }
  }

  private move(newX: number, newY: number): void {
    if (!this.panel) return;
    const panel = this.panel;
    panel.panelX = newX;
    panel.panelY = newY;

    const { panelWidth, panelHeight, cellSize, cellPadding } = panel;
    panel.bg.setPosition(newX, newY);
    panel.title.setPosition(newX, newY - panelHeight / 2 + 16);
    if (panel.titleIcon) {
      const ICON = 16;
      const GAP = 6;
      panel.titleIcon.setPosition(newX - panel.title.width / 2 - GAP - ICON / 2, panel.title.y);
    }

    const gridX = newX - panelWidth / 2 + 20;
    const gridY = newY - panelHeight / 2 + 40;

    this.slotRects.forEach((rect, slotIndex) => {
      const row = Math.floor(slotIndex / 5);
      const col = slotIndex % 5;
      const x = gridX + col * (cellSize + cellPadding) + cellSize / 2;
      const y = gridY + row * (cellSize + cellPadding) + cellSize / 2;
      rect.setPosition(x, y);
    });

    this.slotTexts.forEach((text, slotIndex) => {
      const row = Math.floor(slotIndex / 5);
      const col = slotIndex % 5;
      const x = gridX + col * (cellSize + cellPadding) + cellSize / 2;
      const y = gridY + row * (cellSize + cellPadding) + cellSize / 2;
      const data = this.slots.get(slotIndex);
      
      // Verificar si el item es único (no stackeable) para posicionar el texto correctamente
      if (data) {
        const itemTemplate = getItemTemplate(data.itemId);
        // Por defecto todos los items son stackeables, excepto si tienen unique: true
        const isUnique = itemTemplate?.unique ?? false;
        const isStackable = !isUnique;
        
        // Si es stackeable, siempre mostrar cantidad (incluso si es 1)
        // Si es único, solo mostrar si es > 1
        const shouldShowQuantity = isStackable || data.quantity > 1;
        
        if (shouldShowQuantity) {
          if (isUnique && data.quantity > 1) {
            // Esquina superior izquierda para items únicos con cantidad > 1
            text.setPosition(x - cellSize / 2 + 4, y - cellSize / 2 + 4);
            text.setOrigin(0, 0);
          } else {
            // Esquina inferior derecha para items stackeables
            text.setPosition(x + cellSize / 2 - 4, y + cellSize / 2 - 4);
            text.setOrigin(1, 1);
          }
        } else {
          // Por defecto, esquina inferior derecha
          text.setPosition(x + cellSize / 2 - 4, y + cellSize / 2 - 4);
          text.setOrigin(1, 1);
        }
      } else {
        // Por defecto, esquina inferior derecha
        text.setPosition(x + cellSize / 2 - 4, y + cellSize / 2 - 4);
        text.setOrigin(1, 1);
      }
    });

    this.slotEquippedIndicators.forEach((indicator, slotIndex) => {
      const row = Math.floor(slotIndex / 5);
      const col = slotIndex % 5;
      const x = gridX + col * (cellSize + cellPadding) + cellSize / 2;
      const y = gridY + row * (cellSize + cellPadding) + cellSize / 2;
      indicator.setPosition(x - cellSize / 2 + 1, y - cellSize / 2 + 1);
    });

    this.slotIcons.forEach((icon, slotIndex) => {
      const row = Math.floor(slotIndex / 5);
      const col = slotIndex % 5;
      const x = gridX + col * (cellSize + cellPadding) + cellSize / 2;
      const y = gridY + row * (cellSize + cellPadding) + cellSize / 2;
      icon.setPosition(x, y);
      // Badge de tipo: esquina inferior-izquierda del slot.
      this.slotTypeBadges.get(slotIndex)?.setPosition(x - cellSize / 2 + 2, y + cellSize / 2 - 4);

      // Actualizar posición del efecto visual si existe
      const slotData = this.slots.get(slotIndex);
      if (slotData) {
        this.effectManager.updateEffectPosition(slotIndex, x, y);
      }
    });

    const infoX = gridX + 5 * (cellSize + cellPadding) + 24;
    const infoY = gridY;
    
    // Actualizar infoX e infoY en el objeto panel
    panel.infoX = infoX;
    panel.infoY = infoY;

    this.itemTitle?.setPosition(infoX, infoY);
    if (this.rarityNameIconActive && this.rarityNameIcon) {
      const titleH = this.itemTitle?.height ?? 14;
      this.rarityNameIcon.setPosition(infoX - 4, infoY + titleH / 2);
    }

    // Ajustar posición del nombre del encantamiento
    if (this.itemEnchantName && this.itemTitle) {
      this.itemEnchantName.setPosition(infoX, infoY + this.itemTitle.height + 2);
    }
    
    // Ajustar posición de itemDetails considerando el nombre del encantamiento
    let detailsY = infoY + 20;
    if (this.itemTitle) {
      detailsY = this.itemTitle.y + this.itemTitle.height + 2;
    }
    if (this.itemEnchantName) {
      detailsY = this.itemEnchantName.y + this.itemEnchantName.height + 5;
    }
    this.itemDetails?.setPosition(infoX, detailsY);

    // Actualizar posiciones de los textos verdes de bonos activos
    this.updateActiveBonusTextPositions();

    // Actualizar posiciones de botones después de reposicionar el texto
    this.updateButtonPositions();
    // Posicionar debug info en la parte inferior del panel
    this.itemDebugInfo?.setPosition(infoX, newY + panelHeight / 2 - 30);
    this.closeButton?.setPosition(newX + panelWidth / 2 - 16, newY - panelHeight / 2 + 8);
    // Ícono de info a la izquierda de la X; su tooltip se cierra al mover.
    this.infoIcon?.setPosition(newX + panelWidth / 2 - 16 - 26, newY - panelHeight / 2 + 8);
    this.hideDebugTooltip();

    // Re-anclar el label de oro abajo-izquierda (debajo de la grilla de slots).
    if (this.goldLabel) {
      const goldPos = this.computeGoldLabelPosition(newX, newY, panelWidth, panelHeight);
      this.goldLabel.setPosition(goldPos.x, goldPos.y);
    }

    // Reconstruir los dynamic detail texts (meta block + footer) en las nuevas
    // posiciones. El re-select es la forma más simple de mantenerlos en sync
    // con la nueva ubicación del panel.
    if (this.selectedSlot !== null) {
      this.selectSlot(this.selectedSlot);
    }
  }
}

